/**
 * Fireworks: the window dims to dusk and rockets climb from the bottom to
 * burst into spheres, rings, drooping willows and crackling glitter. Two
 * canvases: the dusk underneath is redrawn every frame, and the sparks above
 * it are faded a little each frame rather than cleared, so everything that
 * moves leaves a glowing trail.
 */

import { calm, COLORS, rand, scene } from "./scene";

/** The dusk has lifted by here (ms from the start). Rockets are all up by ~3.6 s and have burst by ~4.8 s. */
const SHOW_MS = 6800;
const DUSK_IN_MS = 700;
const DUSK_OUT_MS = 1100;
/** No spark outlives this, so the sky is empty before the dusk has lifted. */
const SPARKS_END_MS = 6300;
/** Trails fade out over this once the last spark has gone. */
const LINGER_MS = 400;
const FRAME_MS = 16.67;
const TAU = Math.PI * 2;

/** Deep blue overhead to a plum glow at the horizon, as "r, g, b". */
const DUSK_TOP = "10, 13, 34";
const DUSK_BOTTOM = "48, 26, 60";
/** [top, bottom] opacity: more on the light theme, so bursts stand out against a near-white page. */
const DUSK_LIGHT = [0.42, 0.24];
const DUSK_DARK = [0.3, 0.16];

/**
 * The share of every trail wiped per 60 Hz frame. It's wiped in steps of at
 * least this much, never less, because tiny wipes round to nothing in an
 * 8-bit canvas and leave ghost trails behind.
 */
const TRAIL_FADE = 0.2;

/** When each volley goes up (ms) and how many rockets it has; the last is a little finale. */
const VOLLEYS: ReadonlyArray<readonly [number, number]> = [[150, 1], [700, 2], [1300, 1], [1900, 2], [2550, 1], [3200, 3]];
const CALM_VOLLEYS: ReadonlyArray<readonly [number, number]> = [[250, 1], [1350, 1], [2700, 1]];
/** The rockets in a volley go up within this many ms of each other. */
const STAGGER_MS = 320;
/** Willows only go on early rockets, since their long droop would outlast the show. */
const WILLOW_BY_MS = 1500;
/** Where rockets go up, as fractions of the width. */
const X_MIN = 0.15;
const X_MAX = 0.85;
/** How high they peak, as fractions of the height from the top: always in the top half. */
const PEAK_TOP = 0.1;
const PEAK_LOW = 0.45;
/** Frames from launch to the top of the climb. */
const CLIMB = [54, 68];
const CALM_CLIMB = [80, 95];
/** Shells burst just short of the top, still rising a touch, as real ones do. */
const BURST_AT = 0.95;
/** A lazy sideways wiggle on the way up: px, and radians per frame. */
const WOBBLE = 1.4;
const WOBBLE_RATE = 0.3;
/** Sparks a climbing rocket sheds per frame. */
const SHED_RATE = 0.7;
const CALM_SHED_RATE = 0.3;
const ROCKET_WIDTH = 2.4;
const ROCKET_GLOW = 0.3;
const ROCKET_GLOW_R = 9;

type Shell = "sphere" | "ring" | "willow" | "crackle";

/** Burst radius as a fraction of the window's shorter side. */
const RADIUS: Record<Shell, readonly [number, number]> = { sphere: [0.15, 0.22], ring: [0.13, 0.19], willow: [0.14, 0.19], crackle: [0.14, 0.19] };
const COUNT: Record<Shell, number> = { sphere: 96, ring: 60, willow: 72, crackle: 54 };
/** Ms each spark burns. */
const LIFE: Record<Shell, readonly [number, number]> = { sphere: [1100, 1500], ring: [1100, 1400], willow: [1900, 2400], crackle: [850, 1150] };
/** Fewer, smaller bursts when calm. */
const CALM_COUNT = 0.38;
const CALM_SIZE = 0.8;
/** Sparks of a two-colour sphere this close to its middle take the second colour, so it reads as a core. */
const CORE = 0.6;
/** A two-colour ring gets a second ring inside it, this fast. */
const INNER_RING = 0.55;
/** The flash as a shell bursts, as a fraction of its radius, and how bright. */
const FLASH_R = 0.6;
const FLASH = 0.5;
const CALM_FLASH = 0.2;

/** What a spark is, which sets how it moves and draws: an index into the tables below. */
const SPARK = 0;
const WILLOW = 1;
const CRACKLE = 2;
const EMBER = 3;
const POP = 4;
const SHED = 5;
/** Speed kept per 60 Hz frame. Willows are slowed hard so they hang and droop, and their embers barely move at all. */
const DRAG = [0.955, 0.93, 0.955, 0.9, 0.9, 0.9];
/** Pull per 60 Hz frame, px. */
const GRAVITY = [0.055, 0.09, 0.055, 0.02, 0.03, 0.05];
/** Line width, px. */
const SIZE: ReadonlyArray<readonly [number, number]> = [[1.8, 2.4], [1.3, 1.8], [1.7, 2.2], [1, 1.5], [1.6, 2.6], [1, 1.6]];
/** The head's glow: how bright per frame, and its radius as a multiple of the width. */
const GLOW = [0.14, 0.08, 0.12, 0, 0.45, 0.1];
const GLOW_R = [4.5, 4, 4.5, 0, 6, 3.5];
/**
 * A spark coasts 1/K times its launch speed before drag stops it, so a burst
 * of radius R launches its sparks at R * K.
 */
const SPARK_K = -Math.log(DRAG[SPARK]);
const WILLOW_K = -Math.log(DRAG[WILLOW]);
/** Sparks fade over the last part of their life and flicker over the last part of that; crackle glitters for longer. */
const FADE_FROM = 0.4;
const FLICKER_FROM = 0.35;
const GLITTER_FROM = 0.6;
/** Glitter blinks on and off this many times a second, each spark out of step with the rest. */
const GLITTER_HZ = 12;
/** Embers each willow spark drops per frame. They hang where they fell, which is what draws its long drooping trail. */
const EMBER_RATE = 0.4;
const CALM_EMBER_RATE = 0.25;
const EMBER_LIFE = [550, 950];
/**
 * An ember is redrawn every frame over its own fading copies, so this is how
 * much each frame adds. Additive glow piles up faster, so it adds less.
 */
const EMBER_ALPHA = 0.45;
const EMBER_ALPHA_ADDITIVE = 0.2;
/** Each crackle spark ends in a few pops, scattered over this many ms. */
const POPS = [2, 3];
const POP_DELAY_MS = 260;
const POP_LIFE = [70, 150];
const POP_SPEED = [0.6, 2];

/** Indexes into the colour tables: the palette, then a white-hot flash and the rockets' orange. */
const WHITE = COLORS.length;
const ORANGE = COLORS.length + 1;
const HEXES = [...COLORS, "#fff6e8", "#f79a3e"];
/** Willows burn gold, as the real ones do. */
const GOLD = Math.max(0, COLORS.indexOf("#f2c446"));
/** How far streaks are lifted toward white on the dark theme, to glow; on the light theme they stay saturated so they show against the page. */
const LIFT_DARK = 0.3;
const SPRITE_PX = 64;

const WAITING = 0;
const CLIMBING = 1;
const BURST = 2;

interface Rocket {
  /** Ms after the start that it goes up. */
  at: number;
  /** Where it goes up and how high it peaks, as fractions of the window, so a resize before launch still keeps it on screen. */
  fx: number;
  fy: number;
  /** Fraction of the window's shorter side. */
  radius: number;
  shell: Shell;
  color: number;
  /** The core or inner ring's colour; the same as `color` for a one-colour shell. */
  color2: number;
  state: number;
  /** The climb, set at launch: start, speeds and pull in px per frame, and frames to the top. */
  x0: number;
  y0: number;
  vx: number;
  vy: number;
  v0: number;
  g: number;
  apex: number;
  age: number;
  px: number;
  py: number;
  phase: number;
  /** Fractional sparks owed to the next frame. */
  shed: number;
}

interface Spark {
  kind: number;
  x: number;
  y: number;
  /** Where it was last frame, so a fast spark draws a streak rather than a dotted line. */
  px: number;
  py: number;
  vx: number;
  vy: number;
  /** Ms. A pop starts below zero and shows once it gets there. */
  age: number;
  life: number;
  color: number;
  size: number;
  /** Fractional embers owed to the next frame for a willow; where it is in its blinking for crackle. */
  carry: number;
}

const within = (range: readonly number[]) => rand(range[0], range[1]);

/** 0 to 1 with a soft start and end, clamped. */
const ease = (x: number) => {
  const c = Math.min(1, Math.max(0, x));
  return c * c * (3 - 2 * c);
};

/** A palette index other than `c` (any, for -1), so neighbouring rockets differ. */
const other = (c: number) => (c < 0 ? Math.floor(Math.random() * COLORS.length) : (c + 1 + Math.floor(Math.random() * (COLORS.length - 1))) % COLORS.length);

/** "r, g, b" for a hex colour, lifted toward white by `lift`. */
function channels(hex: string, lift: number): string {
  return [1, 3, 5].map((i) => Math.round(parseInt(hex.slice(i, i + 2), 16) * (1 - lift) + 255 * lift)).join(", ");
}

function shuffle<T>(xs: T[]): T[] {
  for (let i = xs.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [xs[i], xs[j]] = [xs[j], xs[i]];
  }
  return xs;
}

let glows: HTMLCanvasElement[] | null = null;

/** A soft glow per colour, white-hot in the middle, drawn once: a gradient per spark per frame would be far too slow. */
function glowSprites(): HTMLCanvasElement[] {
  glows ??= HEXES.map((hex) => {
    const sprite = document.createElement("canvas");
    sprite.width = sprite.height = SPRITE_PX;
    const g = sprite.getContext("2d")!;
    const mid = SPRITE_PX / 2;
    const grad = g.createRadialGradient(mid, mid, 0, mid, mid, mid);
    const rgb = channels(hex, 0);
    grad.addColorStop(0, `rgba(${channels(hex, 0.6)}, 1)`);
    grad.addColorStop(0.18, `rgba(${rgb}, 0.75)`);
    grad.addColorStop(0.45, `rgba(${rgb}, 0.2)`);
    grad.addColorStop(1, `rgba(${rgb}, 0)`);
    g.fillStyle = grad;
    g.fillRect(0, 0, SPRITE_PX, SPRITE_PX);
    return sprite;
  });
  return glows;
}

/** Every rocket of the show, up front. Early ones get one of each shell; later ones cycle the rest, so the finale has three different. */
function plan(quiet: boolean): Rocket[] {
  const early = shuffle<Shell>(quiet ? ["willow", "sphere", "ring"] : ["willow", "sphere", "ring", "crackle"]);
  const late = shuffle<Shell>(quiet ? ["sphere", "ring"] : ["sphere", "ring", "crackle"]);
  const rockets: Rocket[] = [];
  let e = 0;
  let l = 0;
  let color = -1;
  for (const [at, n] of quiet ? CALM_VOLLEYS : VOLLEYS) {
    for (let i = 0; i < n; i++) {
      const shell = at < WILLOW_BY_MS ? early[e++ % early.length] : late[l++ % late.length];
      const radius = within(RADIUS[shell]) * (quiet ? CALM_SIZE : 1);
      color = other(color);
      const willow = shell === "willow";
      const main = willow && Math.random() < 0.75 ? GOLD : color;
      rockets.push({
        at: at + rand(0, STAGGER_MS),
        // Each rocket of a volley gets its own slice of the width, so they don't go up on top of each other.
        fx: X_MIN + ((i + rand(0.1, 0.9)) / n) * (X_MAX - X_MIN),
        fy: rand(Math.max(PEAK_TOP, radius + 0.05), PEAK_LOW),
        radius,
        shell,
        color: main,
        color2: !willow && Math.random() < 0.5 ? other(main) : main,
        state: WAITING,
        x0: 0,
        y0: 0,
        vx: 0,
        vy: 0,
        v0: 0,
        g: 0,
        apex: 1,
        age: 0,
        px: 0,
        py: 0,
        phase: 0,
        shed: 0,
      });
    }
  }
  return rockets;
}

export default function play(): Promise<void> {
  const quiet = calm();
  const dark = document.documentElement.dataset.theme === "dark";
  const glow = glowSprites();
  const tints = HEXES.map((hex) => `rgb(${channels(hex, dark ? LIFT_DARK : 0)})`);
  const rockets = plan(quiet);
  /** Sparks in use are sparks[0, live); the rest are spares, so nothing is allocated once the pool has grown. */
  const sparks: Spark[] = [];
  let live = 0;
  const drag = new Float64Array(DRAG.length);
  let clock = 0;
  /** Frames of trail fade not yet applied (see TRAIL_FADE). */
  let fadeOwed = 0;
  let doneAt = -1;
  let fireDone = false;

  function add(kind: number, x: number, y: number, vx: number, vy: number, life: number, color: number): Spark {
    if (live === sparks.length) sparks.push({ kind, x, y, px: x, py: y, vx, vy, age: 0, life, color, size: 0, carry: 0 });
    const s = sparks[live++];
    s.kind = kind;
    s.x = s.px = x;
    s.y = s.py = y;
    s.vx = vx;
    s.vy = vy;
    s.age = 0;
    s.life = Math.max(1, Math.min(life, SPARKS_END_MS - clock));
    s.color = color;
    s.size = within(SIZE[kind]);
    s.carry = Math.random();
    return s;
  }

  function launch(r: Rocket, w: number, h: number) {
    r.x0 = r.px = r.fx * w;
    r.y0 = r.py = h + 8;
    // Pick the climb's length, then the launch speed and pull that bring it to rest exactly at its peak.
    const rise = r.y0 - r.fy * h;
    r.apex = within(quiet ? CALM_CLIMB : CLIMB);
    r.v0 = (2 * rise) / r.apex;
    r.g = (2 * rise) / (r.apex * r.apex);
    r.vx = rand(-0.35, 0.35);
    r.phase = rand(0, TAU);
    r.state = CLIMBING;
  }

  /** Moves a rocket along its arc (worked out from its age, so it peaks where planned at any frame rate) and draws its head. */
  function climb(ctx: CanvasRenderingContext2D, r: Rocket, dt: number) {
    r.age += dt;
    const top = r.apex * BURST_AT;
    const tau = Math.min(r.age, top);
    const x = r.x0 + r.vx * tau + (quiet ? 0 : Math.sin(tau * WOBBLE_RATE + r.phase) * WOBBLE);
    const y = r.y0 - r.v0 * tau + 0.5 * r.g * tau * tau;
    r.vy = r.g * tau - r.v0;

    ctx.globalAlpha = quiet ? 1 : rand(0.75, 1);
    ctx.strokeStyle = tints[ORANGE];
    ctx.lineWidth = ROCKET_WIDTH;
    ctx.beginPath();
    ctx.moveTo(r.px, r.py);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.globalAlpha = Math.min(1, ROCKET_GLOW * dt);
    ctx.drawImage(glow[ORANGE], x - ROCKET_GLOW_R, y - ROCKET_GLOW_R, ROCKET_GLOW_R * 2, ROCKET_GLOW_R * 2);

    // Shed along the whole segment, not just at the head, so the spray stays even however far it moved this frame.
    for (r.shed += (quiet ? CALM_SHED_RATE : SHED_RATE) * dt; r.shed >= 1; r.shed--) {
      const u = Math.random();
      add(SHED, r.px + (x - r.px) * u, r.py + (y - r.py) * u, r.vx * 0.2 + rand(-0.5, 0.5), r.vy * 0.25 + rand(0.3, 1), rand(220, 420), ORANGE);
    }
    r.px = x;
    r.py = y;
    if (r.age >= top) r.state = BURST;
  }

  function burst(ctx: CanvasRenderingContext2D, r: Rocket, w: number, h: number) {
    const { px: x, py: y } = r;
    const radius = r.radius * Math.min(w, h);
    const n = Math.round(COUNT[r.shell] * (quiet ? CALM_COUNT : 1));
    const life = LIFE[r.shell];
    if (r.shell === "ring") {
      // A ring seen at an angle: squashed, then turned.
      const turn = rand(0, Math.PI);
      const cos = Math.cos(turn);
      const sin = Math.sin(turn);
      const flat = rand(0.35, 1);
      const rings = r.color2 === r.color ? 1 : 2;
      for (let ring = 0; ring < rings; ring++) {
        const m = ring ? Math.round(n / 2) : n;
        const speed = radius * SPARK_K * (ring ? INNER_RING : 1);
        for (let i = 0; i < m; i++) {
          const a = (i / m) * TAU;
          const ux = Math.cos(a);
          const uy = Math.sin(a) * flat;
          const v = speed * rand(0.97, 1.03);
          add(SPARK, x, y, r.vx + (ux * cos - uy * sin) * v, r.vy + (ux * sin + uy * cos) * v, within(life), ring ? r.color2 : r.color);
        }
      }
    } else {
      const kind = r.shell === "willow" ? WILLOW : r.shell === "crackle" ? CRACKLE : SPARK;
      const speed = radius * (kind === WILLOW ? WILLOW_K : SPARK_K);
      for (let i = 0; i < n; i++) {
        // A ball of sparks seen from the side: most fly across your view and crowd its rim, a few fly at you and stay near the middle.
        const a = ((i + Math.random()) / n) * TAU;
        const z = rand(-1, 1);
        const p = Math.sqrt(1 - z * z);
        const v = speed * p * rand(0.92, 1.04);
        const color = kind === CRACKLE ? (i % 2 ? r.color2 : r.color) : p < CORE ? r.color2 : r.color;
        add(kind, x, y, r.vx + Math.cos(a) * v, r.vy + Math.sin(a) * v, within(life), color);
      }
    }
    const flash = radius * FLASH_R;
    ctx.globalAlpha = quiet ? CALM_FLASH : FLASH;
    ctx.drawImage(glow[r.shell === "willow" ? GOLD : WHITE], x - flash, y - flash, flash * 2, flash * 2);
  }

  function crackle(s: Spark) {
    const pops = Math.round(within(POPS));
    for (let j = 0; j < pops; j++) {
      const a = rand(0, TAU);
      const v = within(POP_SPEED);
      add(POP, s.x, s.y, s.vx + Math.cos(a) * v, s.vy + Math.sin(a) * v, within(POP_LIFE), WHITE).age = -rand(0, POP_DELAY_MS);
    }
  }

  function step(ctx: CanvasRenderingContext2D, dt: number) {
    const ms = dt * FRAME_MS;
    for (let i = 0; i < live; i++) {
      const s = sparks[i];
      if (s.age < 0) {
        s.age += ms;
        continue;
      }
      s.age += ms;
      if (s.age >= s.life) {
        if (s.kind === CRACKLE) crackle(s);
        // Swap the spent spark out to the spares and look at whatever took its place.
        const last = sparks[--live];
        sparks[live] = s;
        sparks[i--] = last;
        continue;
      }
      s.vx *= drag[s.kind];
      s.vy = s.vy * drag[s.kind] + GRAVITY[s.kind] * dt;
      s.px = s.x;
      s.py = s.y;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      const k = 1 - s.age / s.life;

      if (s.kind === EMBER) {
        ctx.globalAlpha = Math.min(1, k * (dark ? EMBER_ALPHA_ADDITIVE : EMBER_ALPHA) * dt * (quiet ? 1 : rand(0.55, 1)));
        ctx.fillStyle = tints[s.color];
        ctx.fillRect(s.x - s.size / 2, s.y - s.size / 2, s.size, s.size);
        continue;
      }
      if (s.kind === WILLOW) {
        for (s.carry += (quiet ? CALM_EMBER_RATE : EMBER_RATE) * dt; s.carry >= 1; s.carry--) {
          const u = Math.random();
          const x = s.px + (s.x - s.px) * u;
          const y = s.py + (s.y - s.py) * u;
          add(EMBER, x, y, s.vx * 0.1 + rand(-0.12, 0.12), s.vy * 0.1 + rand(-0.12, 0.12), within(EMBER_LIFE) * (0.4 + 0.6 * k), s.color);
        }
      }

      let a = s.kind === POP ? k : Math.min(1, k / FADE_FROM);
      if (!quiet) {
        if (s.kind === CRACKLE && k < GLITTER_FROM) a *= ((s.age * GLITTER_HZ) / 1000 + s.carry) % 1 < 0.5 ? 0.1 : 1;
        else if (k < FLICKER_FROM) a *= rand(0.35, 1);
      }
      ctx.globalAlpha = a;
      ctx.strokeStyle = tints[s.color];
      ctx.lineWidth = s.size * (0.55 + 0.45 * k);
      ctx.beginPath();
      ctx.moveTo(s.px, s.py);
      ctx.lineTo(s.x, s.y);
      ctx.stroke();
      // Scaled by dt: it's drawn every frame on top of its own fading copies, so it would glow brighter at higher frame rates.
      ctx.globalAlpha = Math.min(1, a * GLOW[s.kind] * dt);
      const r = s.size * GLOW_R[s.kind] * (0.6 + 0.4 * k);
      ctx.drawImage(glow[s.color], s.x - r, s.y - r, r * 2, r * 2);
    }
  }

  let sky: CanvasGradient | null = null;
  let skyH = 0;
  const dusk = scene(({ ctx, w, h, t }) => {
    const a = ease(t / DUSK_IN_MS) * ease((SHOW_MS - t) / DUSK_OUT_MS);
    if (a > 0) {
      if (!sky || skyH !== h) {
        const [top, bottom] = dark ? DUSK_DARK : DUSK_LIGHT;
        sky = ctx.createLinearGradient(0, 0, 0, h);
        sky.addColorStop(0, `rgba(${DUSK_TOP}, ${top})`);
        sky.addColorStop(1, `rgba(${DUSK_BOTTOM}, ${bottom})`);
        skyH = h;
      }
      ctx.globalAlpha = a;
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, w, h);
    }
    return t < SHOW_MS || !fireDone;
  });

  const fire = scene(
    ({ ctx, w, h, t, dt }) => {
      clock = t;
      fadeOwed += dt;
      if (fadeOwed >= 1) {
        ctx.globalCompositeOperation = "destination-out";
        ctx.globalAlpha = 1 - Math.pow(1 - TRAIL_FADE, fadeOwed);
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, w, h);
        fadeOwed = 0;
      }
      // Additive glow piles up toward white, which shines on the dark theme but washes out against a light page.
      ctx.globalCompositeOperation = dark ? "lighter" : "source-over";
      ctx.lineCap = "round";
      for (let k = 0; k < DRAG.length; k++) drag[k] = Math.pow(DRAG[k], dt);

      let pending = 0;
      for (const r of rockets) {
        if (r.state === BURST) continue;
        pending++;
        if (r.state === WAITING) {
          if (t < r.at) continue;
          launch(r, w, h);
        }
        climb(ctx, r, dt);
        if (r.state === BURST) burst(ctx, r, w, h);
      }
      step(ctx, dt);

      if (pending || live) return true;
      if (doneAt < 0) doneAt = t;
      fireDone = t - doneAt >= LINGER_MS;
      return !fireDone;
    },
    { clear: false },
  );

  return Promise.all([dusk, fire]).then(() => undefined);
}
